import {users as usersShared} from '@cl/shared';
import {
  DimensionValue,
  InsightsMetricSelectOption,
  MetricAndDimensionOptions,
} from 'common/src/modules/analytics/analytics.types';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {Dashboard} from 'common/src/modules/dashboards/dashboards.types';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {CLStore} from 'common/src/redux/store';
import _map from 'lodash/map';
import partitionByEventType from '../utils/partitionByEventType';
import toNameValueMapping from '../utils/toNameValueMapping';

type AccountAnalyticsContext = {
  dashboards: Dashboard[];
  isUserWithAnalyticsRole: boolean;
  metricsAndDimensionsOptions: MetricAndDimensionOptions;
  filters: InsightsMetricSelectOption<DimensionValue>[];
};

function accountAnalyticsContext(store: CLStore): AgentContext<AccountAnalyticsContext> {
  function getter(): AccountAnalyticsContext {
    const {dashboardPage} = store.getState();

    const isUserWithAnalyticsRole = doesUserHaveRole(
      usersShared.userSubject.data,
      RoleTypeName.AnalyticsWrite,
    );

    const {dashboards, metricsAndDimensionsOptions, filters} = dashboardPage;

    return {
      dashboards,
      isUserWithAnalyticsRole,
      metricsAndDimensionsOptions,
      filters,
    };
  }

  function formatter({currentContext}: {currentContext: AccountAnalyticsContext}): string {
    if (!currentContext) {
      return '';
    }

    const {dashboards, isUserWithAnalyticsRole, metricsAndDimensionsOptions, filters} =
      currentContext;

    const [adServingMetrics, websiteMetrics] = partitionByEventType(
      metricsAndDimensionsOptions.metrics,
    );

    const [adServingDimensions, websiteDimensions] = partitionByEventType(
      metricsAndDimensionsOptions.dimensions,
    );

    const [adServingFilters, websiteFilters] = partitionByEventType(filters);

    return `
      - User has analytics write role: ${isUserWithAnalyticsRole}

      - Available widgets and their limitations:
        1.Pie (up to 1 metric up to 1 dimension);
        2.Line (up to 2 metrics up to 1 dimension);
        3.Bar stacked (up to 2 metrics up to 1 dimension);
        4.Horizontal Bar (up to 2 metrics up to 1 dimension);
        5.Multi Table (up to 10 metrics and 3 dimensions).

      - Account dashboards: ${_map(dashboards, (dashboard) => `${dashboard.name || 'Dashboard'} ID: ${dashboard.id} ${dashboard.readOnly ? '(Read Only)' : ''}`).join(', ')}
        - Ad serving event type metrics mapping: ${JSON.stringify(toNameValueMapping(adServingMetrics))}
        - Website event type metrics mapping: ${JSON.stringify(toNameValueMapping(websiteMetrics))}
        - Ad serving event type viewing dimensions mapping: ${JSON.stringify(
          toNameValueMapping(adServingDimensions),
        )}
        - Website event type viewing dimensions mapping: ${JSON.stringify(
          toNameValueMapping(websiteDimensions),
        )}
        - Ad serving event type filtering dimensions mapping: ${JSON.stringify(
          toNameValueMapping(adServingFilters),
        )}
        - Website event type filtering dimensions mapping: ${JSON.stringify(
          toNameValueMapping(websiteFilters),
        )}
 
      )}
      `;
  }

  return {
    title: 'Current account analytics context',
    getter,
    formatter,
  };
}

export default accountAnalyticsContext;
